'use client';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { forwardRef } from 'react';
import { VariantProps } from 'tailwind-variants';

import { iconButtonStyle } from './icon-button.styles';

import { Icon } from '@/components/ui/icon.component';
import { Link } from '@/components/ui/link/link.component';

type IconButtonVariants = VariantProps<typeof iconButtonStyle>;

interface IconButtonBaseProps extends Omit<IconButtonVariants, 'disabled'> {
  icon: IconDefinition;
  label: string;
  className?: string;
  iconClassName?: string;
}

export interface IconButtonProps
  extends IconButtonBaseProps,
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'disabled'> {
  disabled?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, label, size, disabled, className, iconClassName, ...props }, ref) => {
    const { container, icon: iconStyle } = iconButtonStyle({ size, disabled });

    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        title={label}
        disabled={!!disabled}
        className={container({ className })}
        {...props}
      >
        <Icon icon={icon} className={iconStyle({ className: iconClassName })} />
      </button>
    );
  },
);

IconButton.displayName = 'IconButton';

export interface IconButtonLinkProps
  extends IconButtonBaseProps,
  Omit<React.ComponentProps<typeof Link>, 'children' | 'className'> {}

export function IconButtonLink({
  icon,
  label,
  size,
  className,
  iconClassName,
  ...props
}: IconButtonLinkProps) {
  const { container, icon: iconStyle } = iconButtonStyle({ size });

  return (
    <Link aria-label={label} title={label} className={container({ className })} {...props}>
      <Icon icon={icon} className={iconStyle({ className: iconClassName })} />
    </Link>
  );
}
